import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

import { useReportHistory } from "../../../hooks/useReportHistory";
import {
  REPORT_HISTORY_STATUS,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from "../../../services/case-manager/reportHistory";
import { getGeneratedReport } from "../../../services/case-manager/oorReport";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue,
} from "../../ui/select";
import { parseDateSafe } from "../../../utils/dateFormatters";
import { buildReportPdfFromDetail } from "./reportPdfFromDetail";
import {
  REPORT_VIEWS,
  formatReportNumber,
  reportViewLabel,
} from "./reportViews";
import styles from "./ReportHistory.module.css";

// SINGLE final-state badge per entry (Devon: "one final state badge").
// Closed reports use "Report Closed without Submitting" in a neutral color
// (p.joshi: not "discarded"/red).
const STATUS_BADGE_CLASS = {
  [REPORT_HISTORY_STATUS.SUBMITTED]: "badgeSubmitted",
  [REPORT_HISTORY_STATUS.CLOSED_WITHOUT_SUBMISSION]: "badgeNeutral",
  [REPORT_HISTORY_STATUS.GENERATED]: "badgeGenerated",
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// "2026-05-10" -> "10 May 2026"
const fmtDate = (ymd) => {
  const d = parseDateSafe(ymd);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const fmtDateRangeLabel = (start, end) =>
  start || end ? `${fmtDate(start)} - ${fmtDate(end)}` : "—";

// Figma format: "Jan,1 2026, 12:00:00 AM"
const fmtTimestamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${MONTHS[d.getMonth()]},${d.getDate()} ${d.getFullYear()}, ${time}`;
};

// Report fields (location/provider) may come back as a plain string or as
// an { id, name } record — display whichever we're given.
const displayValue = (value) => {
  if (!value) return null;
  return typeof value === "object" ? value.name || value.label || null : String(value);
};

// "Notes edited – Patient 1" -> ["Notes edited", "Patient 1"] so the value
// half can be bolded like the other detail rows (Provider, Account, ...).
const splitLabelValue = (text) => {
  const idx = text.indexOf("–");
  if (idx === -1) return [null, text];
  return [text.slice(0, idx).trim(), text.slice(idx + 1).trim()];
};

// Sub-text rows shown under a timeline event (matches Figma crop). Only the
// event types below get a sub-line; anything else renders title + timestamp
// only. `report` supplies the generate-time fallbacks (location/provider/date
// range) when the event itself doesn't carry its own metadata snapshot.
const EventDetail = ({ event, report }) => {
  const metadata = event.metadata || {};
  switch (event.display_type) {
    case "submitted":
    case "closed_without_submitting":
      if (!event.note) return null;
      return (
        <div className={`${styles.eventSub} ${styles.eventNote}`}>
          Note : {event.note}
        </div>
      );
    case "shared": {
      const shareTo = metadata.recipient_email || metadata.email || metadata.shared_to;
      if (!shareTo) return null;
      return (
        <div className={styles.eventSub}>
          Report shared to <strong>{shareTo}</strong>
        </div>
      );
    }
    case "downloaded":
      // Figma: the Downloaded row shows only title + timestamp (no subtext).
      return null;
    case "edited": {
      const patientNames = Array.isArray(metadata.patient_names)
        ? metadata.patient_names.join(", ")
        : metadata.patient_name || metadata.patient;
      const lines = [];
      if (patientNames) lines.push(["Patient removed", patientNames]);
      // The "edited" note-edit event's `note` already arrives pre-formatted
      // ("Notes edited – <patient>") — split it so the value half can be
      // bolded like the other detail rows.
      if (event.note) {
        const [label, value] = splitLabelValue(event.note);
        lines.push([label || "Notes edited", value]);
      }
      if (!lines.length) return null;
      return (
        <div className={styles.eventSub}>
          {lines.map(([label, value], i) => (
            <div key={i}>
              {label} – <strong>{value}</strong>
            </div>
          ))}
        </div>
      );
    }
    case "generated":
      return (
        <div className={styles.eventSub}>
          <div>
            Location – <strong>{metadata.account_name || displayValue(report.location) || "—"}</strong>
          </div>
          <div>
            Provider –{" "}
            <strong>{metadata.provider_label || displayValue(report.provider) || "All Providers"}</strong>
          </div>
          <div>
            Date range –{" "}
            <strong>
              {metadata.date_range_label || fmtDateRangeLabel(report.date_start, report.date_end)}
            </strong>
          </div>
        </div>
      );
    default:
      return null;
  }
};

// History isn't filtered by report type, so every entry names its own
// (SCRUM-790); Extended Threshold entries add their number once submitted.
const reportTypeLabel = (report) => {
  const label = reportViewLabel(report.report_view);
  if (report.report_view !== REPORT_VIEWS.EXTENDED_THRESHOLD) return label;
  const number = formatReportNumber(report.report_number, report.report_number_display);
  return number ? `${label} #${number}` : label;
};

const ReportEntry = ({ report, onOpenDraft }) => {
  const badgeClass = styles[STATUS_BADGE_CLASS[report.status]] || styles.badgeNeutral;
  // API returns events newest-first already (SCRUM-138 Ordering rule: no
  // client-side sorting).
  const events = report.events || [];
  const [opening, setOpening] = useState(false);
  // SCRUM-790: a generated (not yet submitted) Extended Threshold report is
  // submitted from the main OOR screen, so the link goes back there instead
  // of opening a PDF.
  const opensOnDashboard =
    Boolean(onOpenDraft) &&
    report.report_view === REPORT_VIEWS.EXTENDED_THRESHOLD &&
    report.status === REPORT_HISTORY_STATUS.GENERATED;

  // Redraw the report's PDF from its frozen snapshot and open it in a new tab.
  const openReport = async () => {
    if (opening) return;
    if (opensOnDashboard) {
      onOpenDraft(report);
      return;
    }
    // Open the tab synchronously, inside the click, or the popup is blocked.
    const tab = window.open("", "_blank");
    setOpening(true);
    try {
      const detail = await getGeneratedReport(report.report_id ?? report.id);
      const { blob } = buildReportPdfFromDetail(detail, report);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      tab?.close();
      toast.error("Could not open this report.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className={styles.entry}>
      {/* Top row: single final-state badge (left) + date-range chip (right) */}
      <div className={styles.entryTop}>
        <div className={styles.entryTitle}>
          <span className={`${styles.badge} ${badgeClass}`}>
            Report {report.status_label || report.status}
          </span>
          <span className={styles.reportType}>{reportTypeLabel(report)}</span>
        </div>
        <span className={styles.dateChip}>
          {/* <Calendar size={16} /> */}
          {fmtDateRangeLabel(report.date_start, report.date_end)}
        </span>
      </div>

      <div className={styles.timelineWrap}>
        <ol className={styles.timeline}>
          {events.map((ev, i) => (
            <li key={ev.id ?? i} className={styles.event}>
              <span className={styles.eventDot} aria-hidden="true" />
              <div className={styles.eventBody}>
                <div className={styles.eventHead}>
                  <span className={styles.eventTitle}>
                    {ev.display_label || ev.display_type}{" "}
                    {ev.actor?.name ? (
                      <span className={styles.eventUser}>({ev.actor.name})</span>
                    ) : null}
                  </span>
                  <span className={styles.eventTime}>{fmtTimestamp(ev.created_at)}</span>
                </div>
                <EventDetail event={ev} report={report} />
              </div>
            </li>
          ))}
        </ol>

        {/* "Open this Report" — every entry was previously generated (AC).
            PDFs aren't stored; the link redraws one from the report's
            snapshot (SCRUM-790 contract, all three report types). */}
        <button
          type="button"
          className={styles.openLink}
          onClick={openReport}
          disabled={opening}
        >
          {opening ? "Opening…" : "Open this Report"} <ArrowUpRight size={15} />
        </button>
      </div>
    </div>
  );
};

// SCRUM-920: the request sends page/page_size. Read the total from whichever
// key the API uses (`total_pages`, or a DRF-style `count`); if the response
// carries no pagination meta and returned the whole list, page it here.
const paginate = (data, rows, page, pageSize) => {
  const meta = data?.pagination || data?.meta || data || {};
  if (meta.total_pages != null) {
    return { reports: rows, totalPages: Math.max(Number(meta.total_pages) || 1, 1) };
  }
  const total = meta.count ?? meta.total_count ?? meta.total;
  if (total != null) {
    return { reports: rows, totalPages: Math.max(Math.ceil(Number(total) / pageSize), 1) };
  }
  if (rows.length > pageSize) {
    const start = (page - 1) * pageSize;
    return {
      reports: rows.slice(start, start + pageSize),
      totalPages: Math.ceil(rows.length / pageSize),
    };
  }
  return { reports: rows, totalPages: 1 };
};

// Footer matching the portal's other paginations (Billing): "‹ [page] of N ›"
// on the left, "Result per page [size]" on the right.
const HistoryPagination = ({ page, totalPages, pageSize, disabled, onPageChange, onPageSizeChange }) => (
  <div className={styles.footer}>
    <div className={styles.pager}>
      <button
        type="button"
        className={styles.pagerArrow}
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1 || disabled}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} />
      </button>
      <span className={styles.pagerBox}>{page}</span>
      <span className={styles.pagerOf}>of {totalPages}</span>
      <button
        type="button"
        className={styles.pagerArrow}
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages || disabled}
        aria-label="Next page"
      >
        <ChevronRight size={14} />
      </button>
    </div>

    <div className={styles.perPage}>
      <span className={styles.perPageLabel}>Result per page</span>
      <Select
        className={styles.perPageSelect}
        value={String(pageSize)}
        onChange={(value) => onPageSizeChange(Number(value))}
      >
        <SelectTrigger
          style={{
            width: "72px",
            height: "32px",
            border: "1px solid #d0d0d0",
            borderRadius: "6px",
            backgroundColor: "white",
            fontSize: "13px",
            padding: "4px 8px",
          }}
        >
          <SelectValue placeholder="Page size">{pageSize}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

/**
 * OOR "Out of Range – History" page (SCRUM-138). Read-only. Rendered in place
 * of the report inside the Out of Range tab; `onBack` returns to the report with
 * its filters/date-range/account preserved (the panel never unmounts).
 */
const ReportHistory = ({ onBack, onOpenDraft }) => {
  // SCRUM-920: page 1 / 25 per page by default.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { data, isLoading, isError, isFetching } = useReportHistory({ page, pageSize });
  // Tolerate either key: the sibling OOR report endpoints (oorReport.js) key
  // their row list `results`; this endpoint's own contract says `reports`.
  const rows = data?.reports || data?.results || [];
  const { reports, totalPages } = paginate(data, rows, page, pageSize);

  // Never sit past the last page if the total shrinks between fetches.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={onBack}
          aria-label="Back to report"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <h2 className={styles.title}>Out of Range – History</h2>
      </div>
      <div className={styles.headerDivider} />

      {isLoading ? (
        <div className={styles.state}>Loading report history…</div>
      ) : isError ? (
        <div className={styles.state}>Could not load report history.</div>
      ) : rows.length === 0 ? (
        <div className={styles.state}>No report history yet.</div>
      ) : (
        <>
          <div className={styles.list}>
            {reports.map((r) => (
              <ReportEntry
                key={r.id ?? r.report_id}
                report={r}
                onOpenDraft={onOpenDraft}
              />
            ))}
          </div>
          <HistoryPagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            disabled={isFetching}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default ReportHistory;
